import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Users, Search, Shield, Crown, UserPlus, Trash2, X, UserCheck } from 'lucide-react';

export default function OwnerUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'panzix_employee' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/api/owner/users');
      setUsers(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAddStaff = async () => {
    setSaving(true);
    try {
      await api.post('/api/owner/staff', addForm);
      setShowAddModal(false);
      setAddForm({ name: '', email: '', password: '', role: 'panzix_employee' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add staff');
    }
    finally { setSaving(false); }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.patch(`/api/owner/users/${userId}/role`, { role: newRole });
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user? This cannot be undone.')) return;
    try {
      await api.delete(`/api/owner/users/${userId}`);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Cannot remove'); }
  };

  const getRoleIcon = (role) => {
    if (role === 'owner') return <Crown size={14} color="#F9A825" />;
    if (role === 'panzix_employee') return <Shield size={14} color="#7B1FA2" />;
    if (role === 'admin') return <UserCheck size={14} color="#2E7D32" />;
    return <Users size={14} color="#64748b" />;
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: { bg: '#fef3c7', color: '#92400e' },
      panzix_employee: { bg: '#f3e8ff', color: '#7c3aed' },
      admin: { bg: '#dcfce7', color: '#166534' },
      user: { bg: '#f1f5f9', color: '#475569' }
    };
    const s = styles[role] || styles.user;
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{getRoleIcon(role)}{role}</span>;
  };

  const filtered = users.filter(u => {
    const matchSearch = u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'internal' && u.is_internal) || (filter === 'subscribers' && !u.is_internal) || u.role === filter;
    return matchSearch && matchFilter;
  });

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>All Users ({users.length})</h3>
          <p style={{ fontSize: 13, color: '#64748b' }}>Manage platform users, Panzix staff, and subscriber accounts.</p>
        </div>
        <button className="btn-action" onClick={() => setShowAddModal(true)} data-testid="add-staff-btn">
          <UserPlus size={16} style={{ marginRight: 6 }} />Add Panzix Staff
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
          <input data-testid="search-users" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..." style={{ width: '100%', padding: '10px 12px 10px 36px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="filter-role"
          style={{ padding: '10px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', minWidth: 160 }}>
          <option value="all">All Roles</option>
          <option value="owner">Owners</option>
          <option value="panzix_employee">Panzix Staff</option>
          <option value="admin">Company Admins</option>
          <option value="user">Users</option>
          <option value="internal">Internal Only</option>
          <option value="subscribers">Subscribers Only</option>
        </select>
      </div>

      <table className="audit-log-table" data-testid="users-table">
        <thead>
          <tr><th>Name</th><th>Email</th><th>Role</th><th>Company</th><th>Joined</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {filtered.map(u => (
            <tr key={u.id}>
              <td style={{ fontWeight: 600 }}>{u.name}</td>
              <td>{u.email}</td>
              <td>{getRoleBadge(u.role)}</td>
              <td style={{ fontSize: 12, color: '#64748b' }}>{u.company_name || '-'}</td>
              <td style={{ fontSize: 12 }}>{new Date(u.created_at).toLocaleDateString()}</td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select value={u.role} onChange={e => handleChangeRole(u.id, e.target.value)}
                    style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11 }} data-testid={`role-select-${u.id}`}>
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                    <option value="panzix_employee">panzix_employee</option>
                    <option value="owner">owner</option>
                  </select>
                  <button onClick={() => handleRemoveUser(u.id)} className="btn-delete" style={{ padding: '4px 8px', fontSize: 11 }} data-testid={`remove-${u.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddModal(false)}>
          <div className="task-modal slide-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Add Panzix Staff Member</h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Full Name</label>
              <input data-testid="staff-name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input data-testid="staff-email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@panzix.com" type="email" />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input data-testid="staff-password" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" type="password" />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select data-testid="staff-role" value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                <option value="panzix_employee">Panzix Employee (Support)</option>
                <option value="owner">Owner (Full Access)</option>
              </select>
            </div>
            <button className="btn-action" onClick={handleAddStaff} disabled={saving || !addForm.name || !addForm.email || !addForm.password}
              style={{ width: '100%' }} data-testid="confirm-add-staff">
              {saving ? 'Adding...' : 'Add Staff Member'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
